import React, { useState, useLayoutEffect, useRef, useCallback, useEffect } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Collapse, Nav, NavItem } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import IntlMessages from 'helpers/IntlMessages';
import { useMenu } from 'contexts/MenuContextUtils';
import Icon from './SidebarIcon';

Icon.propTypes = {
  name: PropTypes.string.isRequired,
};

const Sidebar = ({ menuItems, menuHiddenBreakpoint }) => {
  const location = useLocation();
  const intl = useIntl();
  const wrapper = useRef(null);
  const {
    containerClassnames,
    menuClickCount,
    selectedMenuHasSubItems,
    setContainerClassnames,
    changeSelectedMenuHasSubItems,
    toggleMenu,
  } = useMenu();

  const isMobile = containerClassnames.includes('menu-mobile');

  const [selectedParentMenu, setSelectedParentMenu] = useState('');
  const [viewingParentMenu, setViewingParentMenu] = useState('');
  const [collapsedMenus, setCollapsedMenus] = useState([]);

  const handleMainMenuBack = (e) => {
    e.preventDefault();
    const currentClasses = containerClassnames.split(' ').filter((x) => x !== 'sub-show-temporary');
    if (!currentClasses.includes('main-show-temporary')) {
      currentClasses.push('main-show-temporary');
    }
    const nextClasses = currentClasses.join(' ');
    setContainerClassnames(0, nextClasses, false);
    setViewingParentMenu('');
  };

  const getMenuClassesForResize = useCallback(
    (classes) => {
      const currentClasses = classes.split(' ').filter((x) => x !== '');
      const windowWidth = window.innerWidth;
      let nextClasses = [...currentClasses];

      if (windowWidth < menuHiddenBreakpoint) {
        if (!nextClasses.includes('menu-mobile')) {
          nextClasses.push('menu-mobile');
        }
      } else {
        nextClasses = nextClasses.filter((x) => x !== 'menu-mobile');
      }
      return nextClasses;
    },
    [menuHiddenBreakpoint]
  );

  const handleDocumentClick = useCallback((e) => {
    const currentClasses = containerClassnames ? containerClassnames.split(' ').filter(Boolean) : [];
    // This feature should only be active in mobile view.
    if (!currentClasses.includes('menu-mobile')) {
      return;
    }

    const container = wrapper.current;

    let isMenuClick = false;
    // Check if click originated on menu toggle buttons
    if (
      e.target &&
      e.target.classList &&
      (e.target.classList.contains('menu-button') || e.target.classList.contains('menu-button-mobile'))
    ) {
      isMenuClick = true;
    } else if (
      e.target.parentElement &&
      e.target.parentElement.classList &&
      (e.target.parentElement.classList.contains('menu-button') || e.target.parentElement.classList.contains('menu-button-mobile'))
    ) {
      isMenuClick = true;
    } else if (
      e.target.parentElement &&
      e.target.parentElement.parentElement &&
      e.target.parentElement.parentElement.classList &&
      (e.target.parentElement.parentElement.classList.contains('menu-button') || e.target.parentElement.parentElement.classList.contains('menu-button-mobile'))
    ) {
      isMenuClick = true;
    }
    const isTopNavDropdownClick = !!e.target.closest('.user') || !!e.target.closest('.language-button') || !!e.target.closest('.dropdown-menu.show');

    // If the click is outside the menu and not on a toggle button...
    if (container && !(container.contains(e.target) || container === e.target || isMenuClick || isTopNavDropdownClick)) {
      const isMobileMenuOpen = currentClasses.includes('main-show-temporary');

      // ...then ONLY close the menu if it is currently open.
      if (isMobileMenuOpen) {
        setViewingParentMenu('');
        toggleMenu();
      }
    }
  }, [toggleMenu, containerClassnames]);

  useLayoutEffect(() => {
    const handleWindowResize = () => {
      if (window) {
        const nextClassesArray = getMenuClassesForResize(containerClassnames);
        const nextClassesString = nextClassesArray.join(' ');

        if (nextClassesString !== containerClassnames) {
          setContainerClassnames(menuClickCount, nextClassesString, selectedMenuHasSubItems);
        }
      }
    };

    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();

    return () => window.removeEventListener('resize', handleWindowResize);
  }, [containerClassnames, getMenuClassesForResize, menuClickCount, selectedMenuHasSubItems, setContainerClassnames]);

  useEffect(() => {
    ['click', 'touchstart', 'touchend'].forEach((event) =>
      document.addEventListener(event, handleDocumentClick, true)
    );
    return () => {
      ['click', 'touchstart', 'touchend'].forEach((event) =>
        document.removeEventListener(event, handleDocumentClick, true)
      );
    };
  }, [handleDocumentClick]);

  const setSelectedLiActive = useCallback(() => {
    const oldli = document.querySelector('.sub-menu li.active');
    if (oldli) oldli.classList.remove('active');

    const oldliSub = document.querySelector('.third-level-menu li.active');
    if (oldliSub) oldliSub.classList.remove('active');

    const selectedSublink = document.querySelector('.third-level-menu a.active');
    if (selectedSublink) selectedSublink.parentElement.classList.add('active');

    const selectedlink = document.querySelector('.sub-menu a.active');
    if (selectedlink) {
      selectedlink.parentElement.classList.add('active');
      setSelectedParentMenu(selectedlink.parentElement.parentElement.getAttribute('data-parent'));
    } else {
      const selectedParentNoSubItem = document.querySelector('.main-menu li a.active');
      if (selectedParentNoSubItem) {
        setSelectedParentMenu(selectedParentNoSubItem.getAttribute('data-flag'));
      } else {
        const pathname = location.pathname;
        let selectedPath = menuItems[1]?.id || menuItems[0]?.id || '';
        if ((pathname.includes('event') && !pathname.includes('community')) || pathname.includes('course') || pathname.includes('book') || pathname.includes('payment') || pathname.includes('activity') || pathname.includes('join')) {
          selectedPath = menuItems.find((item) => item.id === 'events').id;
        } else if (pathname.includes('community') && !pathname.includes('leaderboard')) {
          selectedPath = menuItems.find((item) => item.id === 'communities').id;
        } else if (pathname.includes('library')) {
          selectedPath = menuItems.find((item) => item.id === 'library').id;
        } else if (pathname === '/stream' || pathname.startsWith('/stream/')) {
          selectedPath = menuItems.find((item) => item.id === 'stream').id;
        } else if (pathname.includes('leaderboard')) {
          selectedPath = menuItems.find((item) => item.id === 'leaderboard').id;
        } else if (pathname.includes('commons')) {
          selectedPath = menuItems.find((item) => item.id === 'commons').id;
        } else if (pathname.includes('ecoregion')) {
          selectedPath = menuItems.find((item) => item.id === 'ecoregions').id;
        }
        setSelectedParentMenu(selectedPath);
      }
    }
  }, [location.pathname, menuItems]);

  useEffect(() => {
    setSelectedLiActive();
  }, [setSelectedLiActive]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const openSubMenu = (e, menuItem) => {
    const selectedParent = menuItem.id;
    const hasSubMenu = menuItem.subs && menuItem.subs.length > 0;

    if (isMobile) {
      if (hasSubMenu) {
        e.preventDefault();
        const currentClasses = containerClassnames.split(' ').filter((x) => x !== 'main-show-temporary');
        if (!currentClasses.includes('sub-show-temporary')) {
          currentClasses.push('sub-show-temporary');
        }
        const nextClasses = currentClasses.join(' ');
        setContainerClassnames(0, nextClasses, true);
        setSelectedParentMenu(selectedParent);
        setViewingParentMenu(selectedParent);
      } else {
        toggleMenu();
      }
      return;
    }

    // The rest of the logic is for Desktop view only
    changeSelectedMenuHasSubItems(hasSubMenu);

    if (!hasSubMenu) {
      setViewingParentMenu(selectedParent);
      setSelectedParentMenu(selectedParent);
      setContainerClassnames(1, 'menu-default sub-hidden', false);
    } else {
      e.preventDefault();
      // If the clicked menu is already open, close it.
      if (selectedParentMenu === selectedParent && !containerClassnames.includes('sub-hidden')) {
        setViewingParentMenu('');
        setContainerClassnames(1, 'menu-default sub-hidden', false);
      } else {
        // Otherwise, open it.
        setContainerClassnames(0, 'menu-default', true);
        setViewingParentMenu(selectedParent);
        setSelectedParentMenu(selectedParent);
      }
    }
  };

  const toggleMenuCollapse = (e, menuKey) => {
    e.preventDefault();
    if (collapsedMenus.includes(menuKey)) {
      setCollapsedMenus(collapsedMenus.filter((x) => x !== menuKey));
    } else {
      setCollapsedMenus([...collapsedMenus, menuKey]);
    }
    return false;
  };

  return (
    <div className="sidebar" ref={wrapper}>
      {/* Main menu */}
      <div className="main-menu rounded-0">
        <div className="scroll">
          <PerfectScrollbar options={{ suppressScrollX: true, wheelPropagation: false }}>
            <Nav vertical className="list-unstyled">
              <ul className="list-unstyled nav flex-column">
                {menuItems &&
                  menuItems.map((item) => (
                    <NavItem
                      as="li"
                      key={item.id}
                      className={classnames({
                        active:
                          (selectedParentMenu === item.id && viewingParentMenu === '') || viewingParentMenu === item.id,
                      })}
                    >
                      {item.newWindow ? (
                        <a
                          href={item.to}
                          className="blue-fill-on-hover"
                          rel="noopener noreferrer"
                          target="_blank"
                          aria-label={intl.formatMessage({ id: item.label })}
                          title={intl.formatMessage({ id: item.label })}
                        >
                          {item.mdiIcon ? (
                            <Icon
                              name={item.mdiIcon}
                              color="transparent"
                              size="2.5rem"
                              style={{
                                strokeWidth: '0.5px',
                                stroke:
                                  (selectedParentMenu === item.id && viewingParentMenu === '') ||
                                  viewingParentMenu === item.id
                                    ? '#60b6aa'
                                    : 'black',
                              }}
                              className="mb-1"
                            />
                          ) : item.svgIcon ? (
                            <Icon
                              name={item.svgIcon}
                              fill={
                                (selectedParentMenu === item.id && viewingParentMenu === '') ||
                                viewingParentMenu === item.id
                                  ? '#60b6aa'
                                  : ''
                              }
                              width="30%"
                              className="mb-1"
                            />
                          ) : (
                            <i className={item.cssIcon} />
                          )}
                          <span className="sidebar-menu-label">
                            <IntlMessages id={item.label} />
                          </span>
                        </a>
                      ) : (
                        <NavLink
                          to={item.to}
                          className="blue-fill-on-hover"
                          onClick={(e) => openSubMenu(e, item)}
                          data-flag={item.id}
                          aria-label={intl.formatMessage({ id: item.label })}
                          title={intl.formatMessage({ id: item.label })}
                        >
                          {item.mdiIcon ? (
                            <Icon
                              name={item.mdiIcon}
                              color="transparent"
                              size="2.5rem"
                              style={{
                                strokeWidth: '0.5px',
                                stroke:
                                  (selectedParentMenu === item.id && viewingParentMenu === '') ||
                                  viewingParentMenu === item.id
                                    ? '#60b6aa'
                                    : 'black',
                              }}
                              className="mb-1"
                            />
                          ) : item.svgIcon ? (
                            <Icon
                              name={item.svgIcon}
                              fill={
                                (selectedParentMenu === item.id && viewingParentMenu === '') ||
                                viewingParentMenu === item.id
                                  ? '#60b6aa'
                                  : ''
                              }
                              width="30%"
                              className="mb-1"
                            />
                          ) : (
                            <i className={item.cssIcon} />
                          )}
                          <span className="sidebar-menu-label">
                            <IntlMessages id={item.label} />
                          </span>
                        </NavLink>
                      )}
                    </NavItem>
                  ))}
              </ul>
            </Nav>
          </PerfectScrollbar>
        </div>
      </div>

      {/* Sub-menu */}
      <div className="sub-menu rounded-0">
        <div className="scroll">
          <PerfectScrollbar options={{ suppressScrollX: true, wheelPropagation: false }}>
              <Nav className="d-block">
                <NavItem>
                  <a href="#back" className="sub-menu-back" onClick={handleMainMenuBack}>
                    <i className="simple-icon-arrow-left" /> <IntlMessages id="general.back" />
                  </a>
                </NavItem>
              </Nav>
            {menuItems &&
              menuItems
                .filter((item) => Array.isArray(item.subs) && item.subs.length > 0)
                .map((item) => (
                  <Nav
                    key={item.id}
                    className={classnames({
                      'd-block':
                        (selectedParentMenu === item.id && viewingParentMenu === '') || viewingParentMenu === item.id,
                    })}
                    data-parent={item.id}
                  >
                    {item.subs.map((sub, index) => (
                      <NavItem
                        key={`${item.id}_${index}`}
                        className={`${sub.subs && sub.subs.length > 0 ? 'has-sub-item' : ''}`}
                      >
                        {sub.newWindow ? (
                          <a href={sub.to} rel="noopener noreferrer" target="_blank">
                            <i className={sub.icon} /> <IntlMessages id={sub.label} />
                          </a>
                        ) : sub.subs && sub.subs.length > 0 ? (
                          <>
                            <NavLink
                              className={`rotate-arrow-icon opacity-50 ${
                                !collapsedMenus.includes(`${item.id}_${index}`) ? '' : 'collapsed'
                              }`}
                              to={sub.to}
                              id={`${item.id}_${index}`}
                              onClick={(e) => toggleMenuCollapse(e, `${item.id}_${index}`)}
                            >
                              <i className="simple-icon-arrow-down" /> <IntlMessages id={sub.label} />
                            </NavLink>
                            <Collapse in={!collapsedMenus.includes(`${item.id}_${index}`)}>
                              <Nav className="third-level-menu">
                                {sub.subs.map((thirdSub, thirdIndex) => (
                                  <NavItem key={`${item.id}_${index}_${thirdIndex}`}>
                                    {thirdSub.newWindow ? (
                                      <a href={thirdSub.to} rel="noopener noreferrer" target="_blank">
                                        <i className={thirdSub.icon} /> <IntlMessages id={thirdSub.label} />
                                      </a>
                                    ) : (
                                      <NavLink to={thirdSub.to}>
                                        <i className={thirdSub.icon} /> <IntlMessages id={thirdSub.label} />
                                      </NavLink>
                                    )}
                                  </NavItem>
                                ))}
                              </Nav>
                            </Collapse>
                          </>
                        ) : (
                          <NavLink to={sub.to}>
                            <i className={sub.icon} /> <IntlMessages id={sub.label} />
                          </NavLink>
                        )}
                      </NavItem>
                    ))}
                  </Nav>
                ))}
          </PerfectScrollbar>
        </div>
      </div>
    </div>
  );
};

Sidebar.propTypes = {
  menuItems: PropTypes.array,
  name: PropTypes.string,
  menuHiddenBreakpoint: PropTypes.number,
  subHiddenBreakpoint: PropTypes.number,
};

Sidebar.defaultProps = {
  menuHiddenBreakpoint: 768,
  subHiddenBreakpoint: 1440,
};

export default Sidebar;
